import { useState, useEffect } from "react";
import { X, ArrowLeft } from "lucide-react";

import SocialLoginButtons from "../components/Forms/SocialLoginButtons";
import LoginForm from "../components/Forms/LoginForm";
import RegisterForm from "../components/Forms/RegisterForm";
import ForgotPasswordForm from "../components/Forms/ForgotPasswordForm";

export default function AuthModal({ onClose, onLoginSuccess }) {
  const [tab, setTab] = useState("login");
  const [error, setError] = useState("");

  // =========================
  // ESC CLOSE
  // =========================
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  // =========================
  // PREVENT BACKGROUND SCROLL
  // =========================
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  // =========================
  // CHANGE TAB
  // =========================
  const changeTab = (newTab) => {
    setError("");
    setTab(newTab);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="
          bg-white
          rounded-2xl
          shadow-xl
          w-full
          relative
          transform
          transition-all
          duration-200
          flex
          flex-col
          h-[580px]
          max-w-xl
          overflow-hidden
        "
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* =========================
            HEADER
        ========================= */}
        <div
          className="
            flex
            items-center
            justify-between
            border-b
            border-[#E8DCCB]
            px-6
            py-4
            flex-shrink-0
            bg-[#F8F3EA]
          "
        >
          <div className="flex items-center gap-3 flex-1">
            {tab === "forgot" && (
              <button
                type="button"
                onClick={() => changeTab("login")}
                className="
                  text-[#3B2A1F]
                  hover:text-[#B88746]
                  transition
                "
                aria-label="Back to login"
              >
                <ArrowLeft size={20} />
              </button>
            )}

            <h2
              className="
                text-lg
                font-semibold
                text-[#3B2A1F]
                text-center
                flex-1
              "
            >
              {tab === "login"
                ? "Log in"
                : tab === "register"
                ? "Register"
                : "Forgot Password"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="
              text-[#8A7664]
              hover:text-[#B88746]
              transition
            "
            aria-label="Close"
          >
            <X size={20} />
          </button>

          <div className="w-5" />
        </div>

        {/* =========================
            CONTENT
        ========================= */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* =========================
              LOGIN / REGISTER TABS
          ========================= */}
          {tab !== "forgot" && (
            <div className="flex justify-center mb-6">
              <div
                className="
                  flex
                  border
                  border-[#D8C8B5]
                  rounded-md
                  overflow-hidden
                  w-80
                "
              >
                <button
                  type="button"
                  className={`
                    w-1/2
                    py-3
                    text-sm
                    font-medium
                    transition
                    ${
                      tab === "register"
                        ? "bg-[#F8F3EA] text-[#3B2A1F]"
                        : "text-[#8A7664] hover:text-[#3B2A1F]"
                    }
                  `}
                  onClick={() => changeTab("register")}
                >
                  Register
                </button>

                <button
                  type="button"
                  className={`
                    w-1/2
                    py-3
                    text-sm
                    font-medium
                    transition
                    ${
                      tab === "login"
                        ? "bg-[#F8F3EA] text-[#3B2A1F]"
                        : "text-[#8A7664] hover:text-[#3B2A1F]"
                    }
                  `}
                  onClick={() => changeTab("login")}
                >
                  Log in
                </button>
              </div>
            </div>
          )}

          {/* =========================
              LOGIN
          ========================= */}
          {tab === "login" && (
            <>
              <SocialLoginButtons
                onLoginSuccess={onLoginSuccess}
                setError={setError}
                error={error}
              />

              <div className="flex items-center my-6">
                <hr className="flex-1 border-[#E8DCCB]" />

                <span className="px-3 text-sm text-[#8A7664] whitespace-nowrap">
                  or with your email address
                </span>

                <hr className="flex-1 border-[#E8DCCB]" />
              </div>

              <LoginForm
                onLoginSuccess={onLoginSuccess}
                setTab={setTab}
                error={error}
                setError={setError}
              />
            </>
          )}

          {/* =========================
              REGISTER
          ========================= */}
          {tab === "register" && (
            <>
              <SocialLoginButtons
                onLoginSuccess={onLoginSuccess}
                setError={setError}
                error={error}
              />

              <div className="flex items-center my-6">
                <hr className="flex-1 border-[#E8DCCB]" />

                <span className="px-3 text-sm text-[#8A7664] whitespace-nowrap">
                  or with your email address
                </span>

                <hr className="flex-1 border-[#E8DCCB]" />
              </div>

              <RegisterForm />
            </>
          )}

          {/* =========================
              FORGOT PASSWORD
          ========================= */}
          {tab === "forgot" && (
            <ForgotPasswordForm switchTab={setTab} />
          )}
        </div>
      </div>
    </div>
  );
}